/**
 * MSAL Configuration for Hood Memorial Hospital Lunch Ordering System
 *
 * This configures Microsoft Authentication Library (MSAL) for Entra ID SSO.
 * All sensitive values are loaded from environment variables (VITE_ prefix).
 */

import { Configuration, LogLevel, PopupRequest } from '@azure/msal-browser';

// Validate required environment variables at module load time
const ENTRA_CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || '';
const ENTRA_TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID || '';
const ENTRA_AUTHORITY = import.meta.env.VITE_ENTRA_AUTHORITY || 'https://login.microsoftonline.com';
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI || '';

if (!ENTRA_CLIENT_ID) {
  console.error('[MSAL Config] VITE_ENTRA_CLIENT_ID is not set. Authentication will not work.');
}
if (!ENTRA_TENANT_ID) {
  console.error('[MSAL Config] VITE_ENTRA_TENANT_ID is not set. Authentication will not work.');
}

export const msalConfig: Configuration = {
  auth: {
    clientId: ENTRA_CLIENT_ID,

    authority: `${ENTRA_AUTHORITY}/${ENTRA_TENANT_ID}`,

    // Redirect URI for redirect flow (root path for automatic SSO)
    // CRITICAL: Must match Azure AD app registration redirect URIs exactly
    redirectUri: typeof window !== 'undefined'
      ? window.location.origin
      : REDIRECT_URI,

    postLogoutRedirectUri: typeof window !== 'undefined'
      ? window.location.origin
      : REDIRECT_URI,

    // CRITICAL: Set to false to stay on current page after redirect
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // Use sessionStorage for better security (cleared when browser closes)
    cacheLocation: 'sessionStorage',

    // Set to true for IE11 or Edge legacy
    storeAuthStateInCookie: false,
  },
  system: {
    // Logging configuration - increase to Info for debugging
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return; // Don't log PII
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL Error]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL Warning]', message);
            break;
          case LogLevel.Info:
            console.info('[MSAL Info]', message);
            break;
          case LogLevel.Verbose:
            console.debug('[MSAL Verbose]', message);
            break;
        }
      },
      logLevel: LogLevel.Info, // Increased for better debugging
      piiLoggingEnabled: false,
    },
    // Allow native broker (Windows only)
    allowNativeBroker: false,

    // Popup window configuration to ensure proper sizing and features
    windowHashTimeout: 60000,
    iframeHashTimeout: 10000,
    loadFrameTimeout: 10000,
  },
};

/**
 * Scopes to request during login
 *
 * - openid: Required for OIDC
 * - profile: Basic profile info
 * - email: User's email address
 * - User.Read: Read user's Graph profile
 */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
  redirectUri: typeof window !== 'undefined'
    ? window.location.origin
    : REDIRECT_URI,
  // Popup window configuration (only used when popup is explicitly requested)
  popupWindowAttributes: {
    popupSize: {
      height: 600,
      width: 500,
    },
    popupPosition: {
      top: 100,
      left: 100,
    },
  },
};

/**
 * Scopes for Microsoft Graph API calls
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  scopes: ['User.Read'],
};

/**
 * API configuration
 */
export const apiConfig = {
  baseUrl: '/api/v1',
};
